import { useRef, useState } from 'react';

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import { equipmentApi } from '@entities/equipment';

import { getInitialWarehouseTitle } from '../lib/getInitialWarehouseTitle';
import { shouldSkipCustomWarehouseBlur } from '../lib/shouldSkipCustomWarehouseBlur';

type WarehouseCreatingMode = 'project' | 'custom';

export const useAddWarehouse = () => {
    const { t } = useTranslation();
    const queryClient = useQueryClient();
    const [mode, setMode] = useState<WarehouseCreatingMode>('project');
    const [warehouseTitle, setWarehouseTitle] = useState(() => getInitialWarehouseTitle(t));
    const isWarehouseCreatingRef = useRef(false);
    const contentRef = useRef<HTMLDivElement | null>(null);

    const availableProjectsQuery = useQuery(equipmentApi.getAvailableProjectsQueryOptions());

    const createWarehouseMutation = useMutation({
        mutationFn: equipmentApi.createWarehouse,
        onSuccess: async () => {
            await Promise.all([
                queryClient.invalidateQueries({
                    queryKey: equipmentApi.getEquipmentDataQueryOptions().queryKey,
                }),
                queryClient.invalidateQueries({
                    queryKey: equipmentApi.getTotalInfoQueryOptions().queryKey,
                }),
                queryClient.invalidateQueries({
                    queryKey: equipmentApi.getWarehouseListQueryOptions().queryKey,
                }),
                queryClient.invalidateQueries({
                    queryKey: equipmentApi.getAvailableProjectsQueryOptions().queryKey,
                }),
            ]);
        },
    });

    const resetState = () => {
        setMode('project');
        setWarehouseTitle(getInitialWarehouseTitle(t));
    };

    const createCustomWarehouse = async (close?: () => void) => {
        const title = warehouseTitle.trim();

        if (!title || createWarehouseMutation.isPending || isWarehouseCreatingRef.current) {
            return;
        }

        isWarehouseCreatingRef.current = true;

        try {
            await createWarehouseMutation.mutateAsync({ title });
            resetState();
            close?.();
        } finally {
            isWarehouseCreatingRef.current = false;
        }
    };

    const closeCustomWarehouseInput = async (close: () => void) => {
        if (!warehouseTitle.trim()) {
            close();

            return;
        }

        await createCustomWarehouse(close);
    };

    const handleOpenChange = (isOpen: boolean) => {
        if (isOpen) {
            return;
        }

        if (mode === 'custom' && warehouseTitle.trim()) {
            void createCustomWarehouse();

            return;
        }

        resetState();
    };

    const selectProjectMode = () => {
        if (createWarehouseMutation.isPending || isWarehouseCreatingRef.current) {
            return;
        }

        setWarehouseTitle(getInitialWarehouseTitle(t));
        setMode('project');
    };

    const selectCustomMode = () => {
        if (createWarehouseMutation.isPending || isWarehouseCreatingRef.current) {
            return;
        }

        setMode('custom');
    };

    const createProjectWarehouse = async (projectId: number, close: () => void) => {
        if (createWarehouseMutation.isPending || isWarehouseCreatingRef.current) {
            return;
        }

        isWarehouseCreatingRef.current = true;
        close();

        try {
            await createWarehouseMutation.mutateAsync({ projectId });
        } finally {
            isWarehouseCreatingRef.current = false;
        }
    };

    const handleTitleKeyDown = (
        event: React.KeyboardEvent<HTMLInputElement>,
        close: () => void
    ) => {
        if (event.key === 'Enter') {
            void createCustomWarehouse(close);
        }
    };

    const handleTitleBlur = (event: React.FocusEvent<HTMLInputElement>, close: () => void) => {
        if (shouldSkipCustomWarehouseBlur(event, contentRef.current)) {
            return;
        }

        void closeCustomWarehouseInput(close);
    };

    return {
        t,
        mode,
        warehouseTitle,
        setWarehouseTitle,
        contentRef,
        availableProjectsQuery,
        isWarehouseCreating: createWarehouseMutation.isPending || isWarehouseCreatingRef.current,
        handleOpenChange,
        selectProjectMode,
        selectCustomMode,
        createProjectWarehouse,
        handleTitleKeyDown,
        handleTitleBlur,
    };
};
